import {
  CmpInvoiceDisplayStateEnum,
  CmpInvoiceKindEnum,
  CmpInvoicePaymentRuleKindEnum,
  CmpPaymentStatusEnum,
} from "../../../graphql/types";
import { cmpLongDateFormatter } from "../../common/dashboard/formatters";
import { getCmpDateDisplayTimeZone } from "../../../utils/cmpTimeZones";

// Shared invoice formatting + presentation helpers.
//
// These live here so the manage-invoices table (ManageInvoices.tsx) and the
// invoice document / modals (ManageInvoiceActionModals.tsx) render money, dates,
// document titles, watermarks, and the preview action bar from a single source
// of truth instead of duplicating the logic in both files.

export const currencyFormatter = new Intl.NumberFormat("en-CA", {
  currency: "CAD",
  style: "currency",
});

export const dateFormatter = cmpLongDateFormatter;

export const formatCurrency = (amountInCents?: number | null) =>
  currencyFormatter.format((amountInCents || 0) / 100);

export const formatDate = (utcMilli?: number | null) =>
  utcMilli ? dateFormatter.format(new Date(utcMilli)) : "-";

// Invoice dates are chamber-local days. Show them in the chamber time zone so
// every viewer sees the same day as the invoice document and PDF.
export const formatChamberInvoiceDate = (
  utcMilli: number | null | undefined,
  timeZone: string,
) => utcMilli ? new Intl.DateTimeFormat("en-CA", {
  month: "long", day: "2-digit", year: "numeric", timeZone: getCmpDateDisplayTimeZone(utcMilli, timeZone),
}).format(new Date(utcMilli)) : "-";

export const formatInvoiceDocumentDate = (
  utcMilli: number | null | undefined,
  timeZone: string,
) => utcMilli ? new Intl.DateTimeFormat("en-US", {
  month: "long", day: "2-digit", year: "numeric", timeZone: getCmpDateDisplayTimeZone(utcMilli, timeZone),
}).format(new Date(utcMilli)) : "-";

export const isReceiptEligiblePaymentStatus = (
  status: CmpPaymentStatusEnum,
) =>
  status === CmpPaymentStatusEnum.Paid ||
  status === CmpPaymentStatusEnum.Recorded ||
  status === CmpPaymentStatusEnum.PartiallyRefunded;

export const isSavedSuccessfulInvoicePayment = ({
  status,
}: {
  status: CmpPaymentStatusEnum;
}) => isReceiptEligiblePaymentStatus(status);

export const getLatestSuccessfulInvoicePayment = <
  Payment extends {
    receivedAtUtcMilli: number;
    status: CmpPaymentStatusEnum;
  },
>(
  payments: ReadonlyArray<Payment>,
): Payment | undefined =>
  [...payments]
    .filter(isSavedSuccessfulInvoicePayment)
    .sort(
      (first, second) => second.receivedAtUtcMilli - first.receivedAtUtcMilli,
    )[0];

export const getLocalDateInputValue = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const localDateInputToUtcMilli = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (!match) {
    return undefined;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const localDate = new Date(year, monthIndex, day, 0, 0, 0, 0);

  if (
    localDate.getFullYear() !== year ||
    localDate.getMonth() !== monthIndex ||
    localDate.getDate() !== day
  ) {
    return undefined;
  }

  return localDate.getTime();
};

const getOrdinalDaySuffix = (day: number) => {
  if (day % 100 >= 11 && day % 100 <= 13) {
    return "th";
  }

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// Ordinal "July 1st, 2026" form used by the invoice document's coverage-period
// sentences; mirrors the backend PDF's formatOrdinalLongDate verbatim so the
// on-screen preview and the emailed PDF read identically (spec §4).
export const formatOrdinalLongDate = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return "Not set";
  }

  const date = new Date(utcMilli);

  return `${date.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" })} ${date.getUTCDate()}${getOrdinalDaySuffix(date.getUTCDate())}, ${date.getUTCFullYear()}`;
};

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

const isUtcCalendarDateMonthsAfter = (laterUtcMilli: number, earlierUtcMilli: number) => {
  const later = new Date(laterUtcMilli);
  const earlier = new Date(earlierUtcMilli);
  const monthCount = (later.getUTCFullYear() - earlier.getUTCFullYear()) * 12 +
    later.getUTCMonth() - earlier.getUTCMonth();
  if (monthCount < 1) {
    return false;
  }
  const lastDayOfLaterMonth = new Date(
    Date.UTC(later.getUTCFullYear(), later.getUTCMonth() + 1, 0),
  ).getUTCDate();
  return later.getUTCDate() === Math.min(earlier.getUTCDate(), lastDayOfLaterMonth);
};

// Coverage ends on the first day of the next term (the renewal date), so the
// sentence shows the day before it: a January 1 renewal reads "to December
// 31st". St Andrews imported renewal invoices store the last day itself, one
// day before the cycle anniversary; those already read correctly. Mirrors the
// backend PDF's formatCmpInvoiceCoveragePeriodText verbatim.
export const formatInvoiceCoveragePeriodText = ({
  coveragePeriodEndUtcMilli,
  coveragePeriodStartUtcMilli,
  invoiceKind,
}: {
  coveragePeriodEndUtcMilli?: number | null;
  coveragePeriodStartUtcMilli?: number | null;
  invoiceKind?: CmpInvoiceKindEnum | null;
}) => {
  const storesLastDay = !!coveragePeriodStartUtcMilli && !!coveragePeriodEndUtcMilli &&
    invoiceKind === CmpInvoiceKindEnum.Renewal &&
    isUtcCalendarDateMonthsAfter(
      coveragePeriodEndUtcMilli + DAY_IN_MILLISECONDS, coveragePeriodStartUtcMilli,
    );
  const lastDayUtcMilli = coveragePeriodEndUtcMilli && !storesLastDay
    ? coveragePeriodEndUtcMilli - DAY_IN_MILLISECONDS
    : coveragePeriodEndUtcMilli;
  return `${formatOrdinalLongDate(coveragePeriodStartUtcMilli)} to ${formatOrdinalLongDate(lastDayUtcMilli)}`;
};

export const getRecordedPaymentMethodLabel = (label?: string | null) => {
  const trimmedLabel = (label || "").trim();
  const normalizedLabel = trimmedLabel.toLowerCase();

  if (
    !trimmedLabel ||
    trimmedLabel === "-" ||
    normalizedLabel === "not paid" ||
    normalizedLabel === "paid"
  ) {
    return "";
  }

  return trimmedLabel;
};

export const isCreditedInvoiceDisplayState = (
  displayState?: CmpInvoiceDisplayStateEnum | null,
) =>
  displayState === CmpInvoiceDisplayStateEnum.Refunded ||
  displayState === CmpInvoiceDisplayStateEnum.Credited;

export const isOverdueInvoiceDisplayState = (
  displayState?: CmpInvoiceDisplayStateEnum | null,
) => displayState === CmpInvoiceDisplayStateEnum.Overdue;

export const isAwaitingPaymentInvoiceDisplayState = (
  displayState?: CmpInvoiceDisplayStateEnum | null,
) =>
  displayState === CmpInvoiceDisplayStateEnum.NotPaid ||
  displayState === CmpInvoiceDisplayStateEnum.AwaitingPayment;

export const getInvoiceDocumentStatusLabel = ({
  daysPastDue,
  displayState,
}: {
  daysPastDue?: number | null;
  displayState?: CmpInvoiceDisplayStateEnum | null;
}) => {
  if (displayState === CmpInvoiceDisplayStateEnum.Draft) {
    return "Draft";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Paid) {
    return "Paid";
  }

  if (isCreditedInvoiceDisplayState(displayState)) {
    return "Credited";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Voided) {
    return "Voided";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Waived) {
    return "Waived";
  }

  if (
    isOverdueInvoiceDisplayState(displayState) ||
    (isAwaitingPaymentInvoiceDisplayState(displayState) &&
      daysPastDue &&
      daysPastDue > 0)
  ) {
    return "Overdue";
  }

  if (isAwaitingPaymentInvoiceDisplayState(displayState)) {
    return "Awaiting Payment";
  }

  return "Awaiting Payment";
};

export type InvoiceDocumentMode = "INVOICE" | "PAYMENT_RECEIPT";

// The saved invoice preview and payment receipt are separate documents. A
// paid invoice stays a PAID INVOICE until the caller explicitly requests the
// receipt document.
export const getInvoiceDocumentTitle = ({
  displayState,
  documentMode = "INVOICE",
}: {
  displayState?: CmpInvoiceDisplayStateEnum | null;
  documentMode?: InvoiceDocumentMode;
}) => {
  if (documentMode === "PAYMENT_RECEIPT") {
    return "PAYMENT RECEIPT";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Draft) {
    return "DRAFT INVOICE";
  }

  if (isCreditedInvoiceDisplayState(displayState)) {
    return "CREDIT INVOICE";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Voided) {
    return "VOIDED INVOICE";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Paid) {
    return "PAID INVOICE";
  }

  return "INVOICE";
};

export const getInvoiceWatermark = (
  displayState?: CmpInvoiceDisplayStateEnum | null,
) => {
  if (displayState === CmpInvoiceDisplayStateEnum.Draft) {
    return "DRAFT";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Voided) {
    return "VOIDED";
  }

  if (isCreditedInvoiceDisplayState(displayState)) {
    return "CREDITED";
  }

  return null;
};

export const isGroupInsuranceRule = (
  ruleKind?: CmpInvoicePaymentRuleKindEnum | null,
) => ruleKind === CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly;

export type InvoicePreviewActionKey =
  | "printPdf"
  | "addPayment"
  | "createInvoice"
  | "saveDraft"
  | "pendingLifecycleAction"
  | "emailDocument"
  | "sendInvoice"
  | "sendReceipt";

export type InvoicePreviewPendingAction = "VOID" | "ISSUE_CREDIT";

export type InvoicePreviewAction = {
  key: InvoicePreviewActionKey;
  label: string;
  color: "dark" | "gold" | "green" | "red";
  withEmailPreview?: boolean;
  caption?: string;
  previewSubline?: string;
};

const PRINT_PDF_ACTION: InvoicePreviewAction = {
  key: "printPdf",
  label: "Print PDF",
  color: "gold",
};

const SEND_INVOICE_CAPTION =
  "Invoice has been generated and will be emailed to contact below.";
const RECEIPT_EMAIL_ACTION: InvoicePreviewAction = {
  key: "sendReceipt",
  label: "Email",
  color: "gold",
};
const DOCUMENT_EMAIL_ACTION: InvoicePreviewAction = {
  key: "emailDocument",
  label: "Email",
  color: "gold",
};

// Single source of truth for which buttons the invoice preview shows, per state.
// This list contains only actions shown by the approved invoice preview.
export const getInvoicePreviewActions = ({
  displayState,
  documentMode = "INVOICE",
  ruleKind,
  canApplyPayment,
  canEmailDocument = false,
  canSaveDraft = false,
  canSendInvoice,
  canSendReceipt = false,
  pendingAction,
  sendIsResend,
}: {
  displayState?: CmpInvoiceDisplayStateEnum | null;
  documentMode?: InvoiceDocumentMode;
  hasPayments: boolean;
  ruleKind?: CmpInvoicePaymentRuleKindEnum | null;
  canApplyPayment: boolean;
  canEmailDocument?: boolean;
  // Claude Design hideSaveDraft: a draft opened from a Manage Invoices row has
  // no Save Draft action. Create/Update pages pass their save handler.
  canSaveDraft?: boolean;
  canSendInvoice: boolean;
  canSendReceipt?: boolean;
  pendingAction?: InvoicePreviewPendingAction;
  sendIsResend: boolean;
}): InvoicePreviewAction[] => {
  if (pendingAction) {
    return [
      {
        key: "pendingLifecycleAction",
        label:
          pendingAction === "VOID" ? "Void Invoice" : "Credit / Refund Invoice",
        color: "red",
      },
      PRINT_PDF_ACTION,
    ];
  }

  const groupInsurance = isGroupInsuranceRule(ruleKind);

  // Group insurance is settled out-of-band by direct deposit. The applied
  // preview shows Print PDF only for every document state.
  if (groupInsurance) {
    return [PRINT_PDF_ACTION];
  }

  if (documentMode === "PAYMENT_RECEIPT") {
    return canSendReceipt
      ? [PRINT_PDF_ACTION, RECEIPT_EMAIL_ACTION]
      : [PRINT_PDF_ACTION];
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Draft) {
    const createAction: InvoicePreviewAction = {
      key: "createInvoice",
      label: "Approve/Create Invoice",
      color: "gold",
    };

    return canSaveDraft
      ? [{ key: "saveDraft", label: "Save Draft", color: "dark" }, createAction]
      : [createAction];
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Paid) {
    return canEmailDocument
      ? [PRINT_PDF_ACTION, DOCUMENT_EMAIL_ACTION]
      : [PRINT_PDF_ACTION];
  }

  if (isCreditedInvoiceDisplayState(displayState)) {
    return canEmailDocument
      ? [PRINT_PDF_ACTION, DOCUMENT_EMAIL_ACTION]
      : [PRINT_PDF_ACTION];
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Voided) {
    return canEmailDocument
      ? [PRINT_PDF_ACTION, DOCUMENT_EMAIL_ACTION]
      : [PRINT_PDF_ACTION];
  }

  // Awaiting Payment / Overdue, including partially-paid installment plans.
  const actions: InvoicePreviewAction[] = [];

  if (canApplyPayment) {
    actions.push({ key: "addPayment", label: "Add Payment", color: "green" });
  }

  actions.push(PRINT_PDF_ACTION);

  if (canSendInvoice) {
    actions.push({
      key: "sendInvoice",
      label: sendIsResend ? "Resend Invoice" : "Email",
      color: "gold",
      ...(sendIsResend
        ? {
            withEmailPreview: true,
            caption: SEND_INVOICE_CAPTION,
            previewSubline: "To be sent with invoice",
          }
        : {}),
    });
  }

  return actions;
};
